import { useLocalStorage } from "@src/appV2/lib/utils";
import { useCallback, useEffect, useRef, useState } from "react";

import { DEFAULT_CORNER, STORAGE_KEY } from "../constants";
import { type Corner } from "../types";
import { getClosestCorner } from "../utils";

export function useDragPosition() {
  const [position, setPosition] = useLocalStorage<Corner>(STORAGE_KEY, DEFAULT_CORNER);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [hasDragged, setHasDragged] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Handle drag start
  const handleDragStart = useCallback((event: React.MouseEvent | React.TouchEvent) => {
    event.preventDefault();
    setIsDragging(true);
    setHasDragged(false);

    const clientX = "touches" in event ? event.touches[0].clientX : event.clientX;
    const clientY = "touches" in event ? event.touches[0].clientY : event.clientY;

    setDragStart({ x: clientX, y: clientY });
  }, []);

  // Handle drag move
  const handleDragMove = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (!isDragging || !buttonRef.current) {
        return;
      }

      const clientX = "touches" in event ? event.touches[0].clientX : event.clientX;
      const clientY = "touches" in event ? event.touches[0].clientY : event.clientY;

      // Update button position during drag
      const deltaX = clientX - dragStart.x;
      const deltaY = clientY - dragStart.y;

      // Mark as dragged if moved more than 5px
      if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
        setHasDragged(true);
      }

      buttonRef.current.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
    },
    [isDragging, dragStart]
  );

  // Handle drag end
  const handleDragEnd = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (!isDragging || !buttonRef.current) {
        return;
      }

      const clientX = "touches" in event ? event.changedTouches[0].clientX : event.clientX;
      const clientY = "touches" in event ? event.changedTouches[0].clientY : event.clientY;

      // Calculate new corner
      const newCorner = getClosestCorner(clientX, clientY);

      // Update position (automatically stored in localStorage by useLocalStorage)
      setPosition(newCorner);

      // Reset drag state
      setIsDragging(false);
      buttonRef.current.style.transform = "";

      // Reset hasDragged after a short delay to allow click handler to check it first
      setTimeout(() => {
        setHasDragged(false);
      }, 100);
    },
    [isDragging, setPosition]
  );

  // Set up drag event listeners
  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (event: MouseEvent) => {
        handleDragMove(event);
      };

      const handleMouseUp = (event: MouseEvent) => {
        handleDragEnd(event);
      };

      const handleTouchMove = (event: TouchEvent) => {
        handleDragMove(event);
      };

      const handleTouchEnd = (event: TouchEvent) => {
        handleDragEnd(event);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.addEventListener("touchmove", handleTouchMove);
      document.addEventListener("touchend", handleTouchEnd);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
      };
    }

    return undefined;
  }, [isDragging, handleDragMove, handleDragEnd]);

  return {
    position,
    isDragging,
    hasDragged,
    buttonRef,
    handleDragStart,
  };
}
